import { useEffect, useState } from "react";
import type { Method, Species } from "../types";
import { useLang } from "../i18n/LangContext";
import { useAppState } from "../state/AppState";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { CalendarIcon, ChainIcon, FishIcon, HookIcon } from "../icons";
import { todayIso, type SourceKey } from "../state/filters";

const SPECIES_OPTIONS: { value: "" | Species; key: string }[] = [
  { value: "", key: "allSpecies" },
  { value: "salmon", key: "spcSalmon" },
  { value: "brown_trout", key: "spcBrownTrout" },
  { value: "arctic_char", key: "spcCharS" },
  { value: "sea_trout", key: "spcSeaTrout" },
];

const METHOD_OPTIONS: { value: "" | Method; key: string }[] = [
  { value: "", key: "allMethods" },
  { value: "fly", key: "mthFly" },
  { value: "spinner", key: "mthSpinner" },
  { value: "worm", key: "mthWorm" },
];

const SOURCES: { key: SourceKey; label: string; dotClass: string }[] = [
  { key: "svfr.is", label: "svfr.is", dotClass: "dot--src-svfr" },
  { key: "veidikortid.is", label: "veidikortid.is", dotClass: "dot--src-veidikortid" },
  { key: "lax-a.is", label: "lax-a.is", dotClass: "dot--src-lax-a" },
  { key: "strengir.is", label: "strengir.is", dotClass: "dot--src-strengir" },
  { key: "fishpartner.is", label: "fishpartner.is", dotClass: "dot--src-fishpartner" },
  { key: "fluguveidi.is", label: "fluguveidi.is", dotClass: "dot--src-fluguveidi" },
];

function isoToDate(iso: string): Date | null {
  if (!iso) return null;
  // Parse as local time so the picker shows the day the user actually picked
  // — `new Date("2026-05-11")` would be interpreted as UTC midnight and
  // shift backwards in negative-offset zones.
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function dateToIso(d: Date | null): string {
  if (!d || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export interface FiltersPanelProps {
  onClose?: () => void;
  /** Marks the panel as open so legacy CSS hooks (.filters--open) match. */
  open?: boolean;
  /** True when rendered inside the mobile SwipeableDrawer — toggles header
   *  + sticky action bar and a grabber bar at the top. */
  inSheet?: boolean;
}

export function FiltersPanel({ onClose, open, inSheet }: FiltersPanelProps) {
  const { t } = useLang();
  const { filters, dispatchFilter } = useAppState();
  // On mobile sheet: use comfortable (medium) inputs/buttons so the
  // form is finger-friendly. Desktop sidebar keeps `small` to fit.
  const inputSize: "small" | "medium" = inSheet ? "medium" : "small";

  // Control the date picker's open state so we can dismiss it when the
  // filter sheet itself closes (browser back, ESC, close button). The
  // picker portals to <body>, so without this it would survive any
  // navigation that closed the surrounding sheet.
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    if (!open) setPickerOpen(false);
  }, [open]);

  return (
    <section className={"filters" + (open ? " filters--open" : "") + (inSheet ? " filters--sheet" : "")}>
      {inSheet && (
        <header className="filters__header">
          <span className="filters__grabber" aria-hidden="true" />
          <h2>{t("filters")}</h2>
          <IconButton
            className="filter-close"
            aria-label={t("close")}
            onClick={onClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </header>
      )}
      <div className="filters__body">
        <div className="field field--date">
          <label htmlFor="date-filter">
            <span className="field__icon" aria-hidden="true">
              <CalendarIcon />
            </span>
            <span className="field__label">{t("date")}</span>
          </label>
          <Box sx={{ display: "flex", alignItems: "stretch", gap: 1 }}>
            <DatePicker
              value={isoToDate(filters.date)}
              open={pickerOpen}
              onOpen={() => setPickerOpen(true)}
              onClose={() => setPickerOpen(false)}
              onChange={(d) =>
                dispatchFilter({ type: "set", patch: { date: dateToIso(d) } })
              }
              slotProps={{
                textField: {
                  id: "date-filter",
                  size: inputSize,
                  fullWidth: true,
                  sx: { flex: 1, "& input": { fontSize: 16 } },
                },
                openPickerButton: { size: inputSize },
              }}
            />
            <IconButton
              id="date-clear"
              title={t("clearDate")}
              aria-label={t("clearDate")}
              onClick={() =>
                dispatchFilter({ type: "set", patch: { date: todayIso() } })
              }
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px",
                width: inputSize === "medium" ? 48 : 40,
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <RestartAltIcon fontSize={inputSize === "medium" ? "medium" : "small"} />
            </IconButton>
          </Box>
        </div>

        <FormControlLabel
          className="toggle"
          sx={{
            "& .MuiSwitch-root": { flexShrink: 0 },
            "& .MuiFormControlLabel-label": {
              fontSize: 15,
              flex: 1,
              minWidth: 0,
              whiteSpace: "normal",
              lineHeight: 1.3,
            },
            alignItems: "center",
            ml: 0,
            mr: 0,
            width: "100%",
            py: 0.5,
          }}
          control={
            <Switch
              size={inputSize}
              checked={filters.onlyInSeason}
              onChange={(e) =>
                dispatchFilter({ type: "set", patch: { onlyInSeason: e.target.checked } })
              }
            />
          }
          label={t("onlyInSeason")}
        />

        <div className="field">
          <label htmlFor="species-filter">
            <span className="field__icon" aria-hidden="true">
              <FishIcon />
            </span>
            <span className="field__label">{t("species")}</span>
          </label>
          <TextField
            select
            id="species-filter"
            size={inputSize}
            fullWidth
            value={filters.species}
            onChange={(e) =>
              dispatchFilter({
                type: "set",
                patch: { species: e.target.value as "" | Species },
              })
            }
            SelectProps={{ displayEmpty: true }}
          >
            {SPECIES_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.key as never)}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className="field">
          <label htmlFor="method-filter">
            <span className="field__icon" aria-hidden="true">
              <HookIcon />
            </span>
            <span className="field__label">{t("methods")}</span>
          </label>
          <TextField
            select
            id="method-filter"
            size={inputSize}
            fullWidth
            value={filters.method}
            onChange={(e) =>
              dispatchFilter({
                type: "set",
                patch: { method: e.target.value as "" | Method },
              })
            }
            SelectProps={{ displayEmpty: true }}
          >
            {METHOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.key as never)}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className="field">
          <label>
            <span className="field__icon" aria-hidden="true">
              <ChainIcon />
            </span>
            <span className="field__label">{t("sources")}</span>
          </label>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.75 }}>
            {SOURCES.map((s) => {
              const checked = filters.enabledSources.has(s.key);
              const colorClass = `chip--src-${s.key.replace(".is", "")}`;
              return (
                <Chip
                  key={s.key}
                  label={s.label}
                  size={inputSize}
                  icon={
                    checked ? (
                      <CheckCircleIcon />
                    ) : (
                      <RadioButtonUncheckedIcon />
                    )
                  }
                  onClick={() =>
                    dispatchFilter({ type: "toggleSource", key: s.key })
                  }
                  variant="outlined"
                  // Brand colour class is only applied when active. Inactive
                  // chips fall through to the sx overrides below (neutral
                  // grey + faded), so the brand colour signals "on".
                  className={checked ? colorClass : undefined}
                  aria-pressed={checked}
                  sx={{
                    cursor: "pointer",
                    transition:
                      "opacity .15s ease, color .15s ease, border-color .15s ease, background-color .15s ease",
                    // In the mobile filter sheet, bump the chips up to a
                    // proper tap-target height (~44px) so they're easy to
                    // hit with a thumb. Desktop sidebar keeps the smaller
                    // default driven by the MUI size prop.
                    ...(inSheet && {
                      height: 40,
                      fontSize: 14,
                      "& .MuiChip-icon": { width: 18, height: 18 },
                    }),
                    ...(checked
                      ? { opacity: 1 }
                      : {
                          // Inactive: brand colour is gone, but text AND
                          // border stay white (via currentColor on the
                          // theme's outlined variant). Only opacity tells
                          // the user "off" — no grey tint anywhere.
                          opacity: 0.55,
                          color: "text.primary",
                          "&:hover": { opacity: 0.85 },
                        }),
                  }}
                />
              );
            })}
          </Box>
        </div>
      </div>
      {inSheet && (
        <div className="filters__actions">
          <Button
            className="filters__action filters__action--ghost"
            variant="outlined"
            color="inherit"
            size="large"
            onClick={() => {
              dispatchFilter({ type: "reset" });
              onClose?.();
            }}
          >
            {t("reset")}
          </Button>
          <Button
            className="filters__action filters__action--primary"
            variant="contained"
            color="primary"
            size="large"
            onClick={onClose}
          >
            {t("apply")}
          </Button>
        </div>
      )}
    </section>
  );
}
